import React, { useRef, useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import {
  getWebFontConfig,
  fontFamilyIsLoaded,
  isNotGoogleFont
} from "../theme";
import { useRouter } from "next/router";
import { QUERY_PARAM_KEY } from "utils/constants/query";
import { getConfig } from "utils/env";
import { v4 as uuidv4 } from "uuid";
import { useViewport } from "utils/context";
import { getTrackingMode } from "utils/tracking";
import { isManifestPath } from "utils/route";
import {
  useAppShortlinks,
  useCouponPromotionCodes,
  useCtxReferences,
  useEvents,
  useFeatureGroupFeatures,
  useIntegrations,
  useMerchantAccounts,
  usePayment,
  useProductsPrices,
  useResourceApiKeys,
  useSession,
  useAppEntities,
  useTaxRates,
  useSubscriptions,
  useApplications,
  useAttachments,
  useCampaigns,
  useDatasources,
  useAudiences
} from "../selectors";
import isEmpty from "lodash/isEmpty";
import kebabCase from "lodash/kebabCase";
import { enableBetaSectionsForApp } from "utils/constants/navigation";
import { setCSRF } from "utils/csrf";
import QS from "qs";
import { getCtxResourceAndIDs } from "utils/mapStateToProps";
import { MOCK_REFERENCES } from "utils/constants/request";
import { batchActions } from "redux-batched-actions";
import { loginSuccess } from "actions/auth";
import trimStart from "lodash/trimStart";
import snakeCase from "lodash/snakeCase";

export const useNotOnMount = () => {
  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current) {
      console.log("prepareManifestForEditor");
    } else {
      didMountRef.current = true;
    }
    // observe some values here
  }, []);
};

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useCtxResourceAndIDs = () =>
  useSelector((state) => getCtxResourceAndIDs(state));

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const useIsMounted = () => {
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);
  return isMounted;
};

export const withWebfont = ({ font }) => {
  useEffect(() => {
    if (typeof window !== "undefined" && font) {
      if (!fontFamilyIsLoaded(font.family) && !isNotGoogleFont(font.family)) {
        const WebFont = require("webfontloader");

        WebFont.load({
          google: {
            families: [getWebFontConfig(font)]
          }
        });
      }
    }
  }, [font]);
};

export const usePagePreviewUrl = (url) => {
  const router = useRouter();
  const applicationUUID = router.query[QUERY_PARAM_KEY.APPLICATION_UUID];
  const manifestUUID = router.query[QUERY_PARAM_KEY.PAGE_UUID];
  const canEdit = Boolean(applicationUUID && manifestUUID);
  return canEdit
    ? `${url}?application=${applicationUUID}&page=${manifestUUID}`
    : url;
};

export const usePageEditMode = () => {
  const router = useRouter();
  const applicationUUID = router.query.application;
  const manifestUUID = router.query.page;

  return {
    editable: Boolean(applicationUUID && manifestUUID),
    applicationUUID,
    manifestUUID,
    builderUrl: `${getConfig(
      "WEB_CLIENT_ROOT"
    )}/applications/${applicationUUID}/pages/${manifestUUID}`
  };
};

export const useErrorDialog = () => {
  const user = useSelector((state) => ({
    name: state.getIn(["user", "data", "display_name"]),
    email: state.getIn(["user", "data", "email"])
  }));

  return (error) => {
    const eventId = uuidv4();
    Sentry.captureException(error);
    Sentry.showReportDialog({
      eventId,
      ...user
    });
  };
};

/**
 * Delete this
 */
export const useHoverProps = ({ hover, setHover }) => {
  const { isSmall } = useViewport();

  return isSmall
    ? {}
    : {
        onMouseEnter: () => !hover && setHover(true),
        onMouseLeave: () => hover && setHover(false)
      };
};

export const useTrackingMode = () => {
  const router = useRouter();
  const isEditMode = isManifestPath(router.pathname);
  const appUUID =
    router.query.application || router.query[QUERY_PARAM_KEY.APPLICATION_UUID];
  const pageUUID = router.query.page || router.query[QUERY_PARAM_KEY.PAGE_UUID];

  const hasEditParams = Boolean(appUUID && pageUUID);

  return getTrackingMode(isEditMode || hasEditParams);
};

export const useFieldFocusRef = () => {
  const firstNameRef = useRef(null);
  useEffect(() => {
    firstNameRef.current && firstNameRef.current.focus();
  }, []);
  return firstNameRef;
};

export const ensurePayment = ({ fetchMerchantAccountPayment, id }) => {
  const { resourceUUID, resource, merchantAccountUUID } = useCtxReferences();
  const [loading, setLoading] = useState(false);
  const payment = usePayment(id);
  const canRequest = Boolean(resourceUUID && resource);

  useEffect(() => {
    if (canRequest && !payment && !loading && id && merchantAccountUUID) {
      setLoading(true);
      fetchMerchantAccountPayment(
        {
          resourceUUID,
          resource,
          merchantAccountUUID,
          id
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    payment
  };
};

export const ensureSession = ({ fetchMerchantAccountSession, id }) => {
  const { resourceUUID, resource, merchantAccountUUID } = useCtxReferences();
  const canRequest = Boolean(
    resourceUUID && resource && merchantAccountUUID && id
  );
  const [loading, setLoading] = useState(false);
  const session = useSession(id);

  useEffect(() => {
    if (canRequest && !session && !loading && id) {
      setLoading(true);
      fetchMerchantAccountSession(
        {
          resourceUUID,
          resource,
          merchantAccountUUID,
          id
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    session
  };
};

export const ensureEvents = ({ fetchMerchantAccountEvents, data }) => {
  const { resourceUUID, resource, merchantAccountUUID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resource && merchantAccountUUID);
  const [loading, setLoading] = useState(false);

  const events = useEvents(data.name);

  useEffect(() => {
    if (canRequest && !events.length && !loading) {
      setLoading(true);
      fetchMerchantAccountEvents(
        {
          resourceUUID,
          resource,
          merchantAccountUUID,
          data
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    events
  };
};

export const ensureApplications = ({ fetchApplications }) => {
  const { resourceUUID, resource } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resource);
  const [loading, setLoading] = useState(false);
  const applications = useApplications();

  useEffect(() => {
    if (canRequest && !loading) {
      setLoading(true);
      fetchApplications(
        {
          resourceUUID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    applications
  };
};

export const ensureSubscriptions = ({ fetchSubscriptions }) => {
  const { resourceUUID, resource } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resource);
  const [loading, setLoading] = useState(false);
  const subscriptions = useSubscriptions();

  useEffect(() => {
    if (canRequest && !loading) {
      setLoading(true);
      fetchSubscriptions(
        {
          resourceUUID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    subscriptions
  };
};

export const ensureProductsPrices = (props) => {
  const { fetchMerchantAccountProductsPrices, force } = props;
  const { resourceUUID, resource, merchantAccountUUID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resource && merchantAccountUUID);

  const [loading, setLoading] = useState(false);

  const { products, prices } = useProductsPrices();

  useEffect(() => {
    const needsProdPrices = !products.length || !prices.length;
    if (canRequest && (needsProdPrices || force) && !loading) {
      setLoading(true);
      fetchMerchantAccountProductsPrices(
        {
          resourceUUID,
          resource,
          uuid: merchantAccountUUID
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    products,
    prices
  };
};

export const ensureAppCheckout = ({ fetchAppCheckout }) => {
  const [loading, setLoading] = useState(false);

  const checkout = useSelector((state) =>
    state.getIn(["app", "data", "checkout"])
  );

  useEffect(() => {
    if (isEmpty(checkout.toJS()) && !loading) {
      setLoading(true);
      fetchAppCheckout(() => {
        setLoading(false);
      });
    }
  }, []);

  return checkout;
};

export const ensureAppBuilder = ({ fetchAppBuilder }) => {
  const [loading, setLoading] = useState(false);

  const builder = useSelector((state) =>
    state.getIn(["app", "data", "builder"])
  );

  useEffect(() => {
    if (isEmpty(builder.toJS()) && !loading) {
      setLoading(true);
      fetchAppBuilder(() => {
        setLoading(false);
      });
    }
  }, []);

  return builder;
};

export const ensureCouponsPromotionCodes = ({
  fetchMerchantAccountCouponsPromotionCodes
}) => {
  const { resourceUUID, resource, merchantAccountUUID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resource && merchantAccountUUID);
  const [loading, setLoading] = useState(false);

  const { coupons, promotionCodes } = useCouponPromotionCodes();

  useEffect(() => {
    if (canRequest && !coupons.length && !promotionCodes.length && !loading) {
      setLoading(true);
      fetchMerchantAccountCouponsPromotionCodes(
        {
          resourceUUID,
          resource,
          uuid: merchantAccountUUID
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    coupons,
    promotionCodes
  };
};

export const ensureTaxRates = (props) => {
  const {
    resource: ctxResource,
    resourceUUID: ctxResourceUUID,
    merchantAccount: ctxMerchantAccount
  } = useCtxReferences();
  const { fetchMerchantAccountTaxRates } = props;

  const resource = props.resource || ctxResource;
  const resourceUUID = props.resourceUUID || ctxResourceUUID;
  const merchantAccount = props.merchantAccount || ctxMerchantAccount;

  const canRequest = Boolean(
    resourceUUID && resource && merchantAccount && merchantAccount.uuid
  );
  const [loading, setLoading] = useState(false);

  const taxRates = useTaxRates();

  useEffect(() => {
    if (canRequest && !taxRates.length && !loading) {
      setLoading(true);
      fetchMerchantAccountTaxRates(
        {
          resourceUUID,
          resource,
          uuid: merchantAccount.uuid
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    taxRates
  };
};

export const ensureShortLinks = ({ fetchShortLinks }) => {
  const { resource, resourceID, resourceUUID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resourceID && resource);

  const [loading, setLoading] = useState(false);
  const shortLinks = useAppShortlinks();

  useEffect(() => {
    if (canRequest && !shortLinks.length && !loading) {
      setLoading(true);
      fetchShortLinks(
        {
          resourceUUID,
          resourceID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    shortLinks
  };
};

export const ensureAttachments = ({ fetchAttachments }) => {
  const { resource, resourceID, resourceUUID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resourceID && resource);

  const [loading, setLoading] = useState(false);
  const attachments = useAttachments();

  useEffect(() => {
    if (canRequest && !loading) {
      setLoading(true);
      fetchAttachments(
        {
          resourceUUID,
          resourceID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    attachments
  };
};

export const ensureCampaigns = ({ fetchCampaigns }) => {
  const { resource, resourceID, resourceUUID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resourceID && resource);

  const [loading, setLoading] = useState(false);
  const campaigns = useCampaigns();

  useEffect(() => {
    if (canRequest && !loading) {
      setLoading(true);
      fetchCampaigns(
        {
          resourceUUID,
          resourceID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    campaigns
  };
};

export const ensureDatasources = ({ fetchDatasources }) => {
  const { resource, resourceID, resourceUUID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resourceID && resource);

  const [loading, setLoading] = useState(false);
  const datasources = useDatasources();

  useEffect(() => {
    if (canRequest && !loading) {
      setLoading(true);
      fetchDatasources(
        {
          resourceUUID,
          resourceID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    datasources
  };
};

export const ensureAudiences = ({ fetchAudiences }) => {
  const { resource, resourceID, resourceUUID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resourceID && resource);

  const [loading, setLoading] = useState(false);
  const audiences = useAudiences();

  useEffect(() => {
    if (canRequest && !loading) {
      setLoading(true);
      fetchAudiences(
        {
          resourceUUID,
          resourceID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    audiences
  };
};

export const ensureMerchantAccounts = (props) => {
  const {
    resource: ctxResource,
    resourceID: ctxResourceID,
    resourceUUID: ctxResourceUUID
  } = useCtxReferences();
  const { fetchMerchantAccounts } = props;

  const resourceID = props.resourceID || ctxResourceID;
  const resource = props.resource || ctxResource;
  const resourceUUID = props.resourceUUID || ctxResourceUUID;
  const canRequest = Boolean(resourceUUID && resourceID && resource);

  const [loading, setLoading] = useState(false);

  const merchantAccounts = useMerchantAccounts();

  useEffect(() => {
    if (canRequest && !loading) {
      setLoading(true);
      fetchMerchantAccounts(
        {
          resourceUUID,
          resourceID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    merchantAccounts
  };
};

export const ensureApiKeys = ({ fetchApiKeys }) => {
  const { resource, resourceUUID, resourceID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resourceID && resource);
  const [loading, setLoading] = useState(false);

  const apiKeys = useResourceApiKeys();

  useEffect(() => {
    if (!apiKeys.length && !loading && canRequest) {
      setLoading(true);
      fetchApiKeys(
        {
          resourceUUID,
          resourceID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    apiKeys
  };
};

export const ensureFeatureGroupFeatures = ({ fetchFeatureGroupFeatures }) => {
  const { resource, resourceUUID, resourceID } = useCtxReferences();

  const [loading, setLoading] = useState(false);

  const { features, featureGroups } = useFeatureGroupFeatures();
  const canRequest = Boolean(resourceUUID && resourceID && resource);

  useEffect(() => {
    if ((!featureGroups.length || !features.length) && !loading && canRequest) {
      setLoading(true);

      fetchFeatureGroupFeatures(
        {
          resourceUUID,
          resourceID,
          resource
        },
        () => {
          setLoading(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loading,
    features,
    featureGroups
  };
};

export const ensureAppEntities = ({ fetchAppEntities }) => {
  const [loading, setLoading] = useState(false);

  const entities = useAppEntities();

  useEffect(() => {
    if (!entities.length && !loading) {
      setLoading(true);
      fetchAppEntities(() => {
        setLoading(false);
      });
    }
  }, []);

  return {
    loading,
    entities
  };
};

export const ensureProductsPricesCouponsPromotionCodes = (props) => {
  const {
    resource: ctxResource,
    resourceUUID: ctxResourceUUID,
    merchantAccount: ctxMerchantAccount
  } = useCtxReferences();
  const {
    fetchMerchantAccountProductsPrices,
    fetchMerchantAccountCouponsPromotionCodes,
    force
  } = props;

  const resource = props.resource || ctxResource;
  const resourceUUID = props.resourceUUID || ctxResourceUUID;
  const merchantAccount = props.merchantAccount || ctxMerchantAccount;

  const canRequest = Boolean(
    resourceUUID && resource && merchantAccount && merchantAccount.uuid
  );
  const [loadingProductsPrices, setLoadingProductsPrices] = useState(false);
  const [
    loadingCouponsPromotionCodes,
    setLoadingCouponsPromotionCodes
  ] = useState(false);

  const { products, prices } = useProductsPrices();
  const { coupons, promotionCodes } = useCouponPromotionCodes();

  useEffect(() => {
    const needsProdPrices = !products.length || !prices.length;

    if ((needsProdPrices || force) && !loadingProductsPrices && canRequest) {
      setLoadingProductsPrices(true);
      fetchMerchantAccountProductsPrices(
        {
          resourceUUID,
          resource,
          uuid: merchantAccount.uuid,
          test: props.test
        },
        () => {
          setLoadingProductsPrices(false);
        }
      );
    }
    const needsCoupons = !coupons.length;
    if (needsCoupons && !loadingCouponsPromotionCodes && canRequest) {
      setLoadingCouponsPromotionCodes(true);
      fetchMerchantAccountCouponsPromotionCodes(
        {
          resourceUUID,
          resource,
          uuid: merchantAccount.uuid,
          test: props.test
        },
        () => {
          setLoadingCouponsPromotionCodes(false);
        }
      );
    }
  }, [canRequest]);

  return {
    loadingProductsPrices,
    loadingCouponsPromotionCodes,
    coupons,
    promotionCodes,
    products,
    prices
  };
};

export const useInBeta = () => {
  const { application } = useCtxReferences();
  const email = useSelector((state) => state.getIn(["user", "data", "email"]));

  return (
    application &&
    application.subdomain &&
    email &&
    enableBetaSectionsForApp({
      subdomain: application.subdomain,
      email
    })
  );
};

export const ensureIntegrations = ({ fetchIntegrations }) => {
  const { resource, resourceUUID } = useCtxReferences();
  const canRequest = Boolean(resourceUUID && resource);
  const [loading, setLoading] = useState(false);

  const integrations = useIntegrations();
  /**
   * An integration needs details when its raw values from the source are not present
   */
  const needsDetails = integrations.find(({ raw }) => isEmpty(raw));

  useEffect(() => {
    if (canRequest) {
      if (!integrations.length || needsDetails || !loading) {
        setLoading(true);
        fetchIntegrations(
          {
            resourceUUID,
            resource
          },
          () => {
            setLoading(false);
          }
        );
      }
    }
  }, [canRequest]);

  return {
    loading,
    integrations
  };
};

export const useOAuthInitiateUrl = ({ service, url, params }) => {
  const csrfState = useMemo(() => uuidv4(), []);
  useEffect(() => {
    if (service) {
      setCSRF(service, csrfState);
    }
  }, [service, url]);

  if (service && url) {
    const stringified = QS.stringify(
      {
        ...params,
        state: csrfState
      },
      { addQueryPrefix: true }
    );

    return `${url}${stringified}`;
  } else {
    return "";
  }
};

export const useTabPathPrefix = () => {
  const { query } = useRouter();
  return query.tab || "";
};

export const useModelQueryId = (model) => {
  const { query } = useRouter();

  return query[`${kebabCase(model)}-uuid`];
};

export const useInitMockReducers = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(batchActions([loginSuccess(MOCK_REFERENCES.user)]));
  }, []);
};

export const useCleanAsPath = () => {
  const router = useRouter();
  return router
    ? snakeCase(trimStart(router.asPath.split("?")[0].split("#")[0], "/"))
    : "";
};
